import { Checkbox, Group, Stack, Tooltip } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconFile, IconRefresh } from "@tabler/icons-react"
import { PaginationState, RowSelectionState } from "@tanstack/react-table"
import { Button, ActionIcon } from "@tfg/ui"
import { Dispatch, SetStateAction, useState } from "react"
import { useTranslation } from "react-i18next"

import { ProductResponses } from "@tfg/types/endpoints/products"
import { fetchDataOptionsValidationType } from "@tfg/types/schemas/products"

import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import { ComponentDetailsDrawer } from "~/pages/Brand/Regulations/RDUE/ComponentDetailsDrawer.tsx"
import { useRdueColumns } from "~/pages/Brand/Regulations/RDUE/columns.tsx"
import {
  useSyncProductsMutation,
  useValidateProductMutation,
} from "~/pages/Brand/Regulations/RDUE/hooks.ts"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

interface Props {
  data: ProductResponses.GetRDUEProducts
  fetchDataOptions: fetchDataOptionsValidationType
  setPagination: Dispatch<SetStateAction<PaginationState>>
}

export const ProductsRDUETAble = ({
  data,
  fetchDataOptions,
  setPagination,
}: Props) => {
  const { t } = useTranslation("regulations")

  const [selectCompliant, setSelectCompliant] = useState<boolean>(false)

  const maxComponentCount = data.products.reduce((acc, product) => {
    return Math.max(acc, product.components.length)
  }, 0)

  const [opened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false)

  const [component, setComponent] = useState<ProductResponses.RDUEComponent>()

  const viewComponentDetails = (component: ProductResponses.RDUEComponent) => {
    openDrawer()
    setComponent(component)
  }

  const columns = useRdueColumns(maxComponentCount, viewComponentDetails)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const table = useCustomReactTable({
    state: {
      pagination: {
        pageIndex: fetchDataOptions.skip / fetchDataOptions.take,
        pageSize: fetchDataOptions.take,
      },
      rowSelection,
    },
    initialColumnPinning: { left: ["name"] },
    manualPagination: true,
    data: data.products,
    columns,
    rowCount: data.totalProducts,
    onPaginationChange: setPagination,

    onRowSelectionChange: setRowSelection,
  })

  const selectedProducts = {
    ids: table.getSelectedRowModel().rows.map((row) => row.original.id),
  }

  const { mutate: validateProducts, isPending: isValidating } =
    useValidateProductMutation()
  const { mutate: syncProducts, isPending: isSyncing } =
    useSyncProductsMutation({
      onSuccess: (count: number) => {
        showSuccessNotification({
          message: `${count} products updated`,
        })
      },
      onError: (error) => {
        showAxiosError({
          notificationMessage: `Error syncing products`,
          showNotification: true,
          error,
          notificationTitle: "Error",
        })
      },
    })

  return (
    <Stack>
      <Group>
        <Checkbox
          label={<Stack>{t("lists.labels.selectCompliant")}</Stack>}
          checked={selectCompliant}
          onChange={(event) => {
            setSelectCompliant(event.currentTarget.checked)
            table.getRowModel().rows.forEach((row) => {
              if (event.currentTarget.checked) {
                row.toggleSelected(row.original.rdueCompliance === "READY")
              } else {
                row.toggleSelected(false)
              }
            })
          }}
        />
        <Button
          onClick={() => {
            validateProducts(selectedProducts)
          }}
          loading={isValidating}
          toolTip={t("lists.labels.dueDiligence", {
            count: selectedProducts.ids.length,
          })}
          icon={
            <Tooltip
              label={t("lists.labels.dueDiligence", {
                count: selectedProducts.ids.length,
              })}
            >
              <IconFile />
            </Tooltip>
          }
        >
          {t("lists.labels.dueDiligence")}
        </Button>
        <ActionIcon
          icon={<IconRefresh />}
          toolTip="Sync products"
          onClick={syncProducts}
          loading={isSyncing}
        />
      </Group>
      {component && (
        <ComponentDetailsDrawer
          component={component}
          onClose={closeDrawer}
          opened={opened}
        />
      )}

      <CustomTable
        table={table}
        pagination={true}
        manualPaginationCount={data.totalProducts}
      />
    </Stack>
  )
}
